import React from "react";
import { toast } from "sonner";
import { Plus, FloppyDisk, X } from "@phosphor-icons/react";
import { useLang } from "@/contexts/LangContext";
import api from "@/lib/api";
import PrayersAdminList from "@/components/PrayersAdminList";
import RichTextEditor from "@/components/RichTextEditor";

const NEW_CATEGORY = "__new__";
const EMPTY = { title: "", categorySelect: "", newCategory: "", content: "", editingId: null };
const PRAYERS_CHANGED = "soyapostol-prayers-changed";

/**
 * Admin-only management UI. All operations go through the backend so the
 * data lives in MongoDB and survives reloads. The component emits a
 * `soyapostol-prayers-changed` window event after each mutation so the
 * public list refetches without a page reload.
 */
export default function PrayersAdmin({ apiCategories }) {
    const { t, lang } = useLang();
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [form, setForm] = React.useState(EMPTY);
    const [submitting, setSubmitting] = React.useState(false);
    const [filter, setFilter] = React.useState("");

    const refresh = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/prayers/admin/all?lang=${lang}`);
            setItems(res.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [lang]);

    React.useEffect(() => { refresh(); }, [refresh]);

    const apiCategoryNames = (apiCategories || []).map((c) => c.category);
    const allCategories = Array.from(new Set([
        ...apiCategoryNames,
        ...items.map((p) => p.category),
    ])).filter(Boolean).sort((a, b) => a.localeCompare(b, lang === "es" ? "es" : "en"));

    const resetForm = () => setForm(EMPTY);

    const onEdit = (p) => {
        setForm({
            title: p.title,
            categorySelect: allCategories.includes(p.category) ? p.category : NEW_CATEGORY,
            newCategory: allCategories.includes(p.category) ? "" : p.category,
            content: p.content,
            editingId: p.id,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onDelete = async (p) => {
        if (!window.confirm(t("admin.confirm_delete", { title: p.title }))) return;
        try {
            await api.delete(`/prayers/admin/${p.id}`);
            toast.success(t("admin.deleted"));
            await refresh();
            window.dispatchEvent(new Event(PRAYERS_CHANGED));
            if (form.editingId === p.id) resetForm();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const title = form.title.trim();
        const category = form.categorySelect === NEW_CATEGORY
            ? form.newCategory.trim()
            : form.categorySelect.trim();
        const content = form.content.trim();
        if (!title || !category || !content) {
            toast.error(t("admin.missing_fields"));
            return;
        }
        setSubmitting(true);
        try {
            if (form.editingId) {
                await api.patch(`/prayers/admin/${form.editingId}`, { title, category, content });
                toast.success(t("admin.updated"));
            } else {
                await api.post("/prayers/admin", { title, category, content, lang });
                toast.success(t("admin.created"));
            }
            resetForm();
            await refresh();
            window.dispatchEvent(new Event(PRAYERS_CHANGED));
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const visibleItems = React.useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (p) => p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q),
        );
    }, [items, filter]);

    return (
        <section
            className="surface-card p-6 sm:p-7 mb-12 border-sangre/30"
            data-testid="prayers-admin-panel"
        >
            <p className="label-eyebrow mb-3">{t("admin.eyebrow")}</p>
            <h2 className="heading-serif text-2xl tracking-tight mb-1">
                {form.editingId ? t("admin.edit_prayer") : t("admin.new_prayer")}
            </h2>
            <p className="text-xs text-stoneMuted mb-5">
                {t("admin.db_hint")}
            </p>

            <form
                onSubmit={onSubmit}
                className="space-y-4"
                data-testid="prayers-admin-form"
            >
                <div>
                    <label className="label-eyebrow block mb-2">{t("admin.title")}</label>
                    <input
                        type="text"
                        required
                        value={form.title}
                        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                        data-testid="prayers-admin-title"
                        className="w-full px-3 py-2.5 bg-white border border-sand-300 rounded-md ui-sans text-sm focus:outline-none focus:border-sangre"
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="label-eyebrow block mb-2">{t("admin.category")}</label>
                        <select
                            required
                            value={form.categorySelect}
                            onChange={(e) => setForm((f) => ({ ...f, categorySelect: e.target.value }))}
                            data-testid="prayers-admin-category-select"
                            className="w-full px-3 py-2.5 bg-white border border-sand-300 rounded-md ui-sans text-sm focus:outline-none focus:border-sangre"
                        >
                            <option value="">{t("admin.choose_category")}</option>
                            {allCategories.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                            <option value={NEW_CATEGORY}>{t("admin.new_category")}</option>
                        </select>
                    </div>
                    {form.categorySelect === NEW_CATEGORY && (
                        <div>
                            <label className="label-eyebrow block mb-2">{t("admin.new_category_name")}</label>
                            <input
                                type="text"
                                required
                                value={form.newCategory}
                                onChange={(e) => setForm((f) => ({ ...f, newCategory: e.target.value }))}
                                data-testid="prayers-admin-new-category"
                                className="w-full px-3 py-2.5 bg-white border border-sand-300 rounded-md ui-sans text-sm focus:outline-none focus:border-sangre"
                            />
                        </div>
                    )}
                </div>

                <div>
                    <label className="label-eyebrow block mb-2" htmlFor="prayer-rich-editor-area">
                        {t("admin.content")}
                    </label>
                    <RichTextEditor
                        value={form.content}
                        onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                        placeholder={t("admin.content_placeholder")}
                        ariaLabel={t("admin.content")}
                        testId="prayers-admin-content"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="prayers-admin-submit"
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {form.editingId ? <FloppyDisk size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
                        {form.editingId ? t("admin.save") : t("admin.add")}
                    </button>
                    {form.editingId && (
                        <button
                            type="button"
                            onClick={resetForm}
                            data-testid="prayers-admin-cancel"
                            className="ui-sans text-sm inline-flex items-center gap-1.5 px-4 py-2 border border-sand-300 rounded-md hover:border-sangre"
                        >
                            <X size={14} /> {t("admin.cancel")}
                        </button>
                    )}
                </div>
            </form>

            <PrayersAdminList
                items={items}
                visibleItems={visibleItems}
                loading={loading}
                filter={filter}
                setFilter={setFilter}
                onEdit={onEdit}
                onDelete={onDelete}
                t={t}
            />
        </section>
    );
}
